<template>
  <div :class="['gambit-type-group', { 'is-compact': isCompact }]">
    <h4 v-if="title">{{ title }}</h4>

    <div class="game-type-row">
      <GamChip
        v-for="(item, index) in resolvedOptions"
        :key="`gambit-type-${index}`"
        :label="item.label"
        :size="isCompact ? GamChipSize.SMALL : GamChipSize.NORMAL"
        :is-active="selectedTypes.includes(item.value)"
        @click="() => toggleGambitType(item.value)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { GambitType } from '@/core/data/gambit/gambit.type';
import GamChip from '@/views/components/GamChip.vue';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import { computed } from 'vue';

type GambitTypeOption = {
  value: GambitType;
  label: string;
};

const props = withDefaults(
  defineProps<{
    modelValue?: GambitType[];
    options?: GambitTypeOption[];
    title?: string;
    isStreamingHidden?: boolean;
    isCompact?: boolean;
  }>(),
  {
    modelValue: () => [GambitType.ANY],
    title: undefined,
    options: () => [
      { value: GambitType.ANY, label: 'gambit.detail.tag.category.any' },
      { value: GambitType.TIMED, label: 'gambit.detail.tag.category.timed' },
      { value: GambitType.BLITZ, label: 'gambit.detail.tag.category.blitz' },
      { value: GambitType.RAPID, label: 'gambit.detail.tag.category.rapid' },
      { value: GambitType.BULLET, label: 'gambit.detail.tag.category.bullet' },
      { value: GambitType.CLASSIC, label: 'gambit.detail.tag.category.classic' },
      { value: GambitType.STREAMED, label: 'gambit.detail.tag.category.streamed' },
      { value: GambitType.FREESTYLE, label: 'gambit.detail.tag.category.freestyle' },
      { value: GambitType.BUGHOUSE, label: 'gambit.detail.tag.category.bughouse' },
      { value: GambitType.HAND_AND_BRAIN, label: 'gambit.detail.tag.category.hand_and_brain' },
      { value: GambitType.STIMUL, label: 'gambit.detail.tag.category.stimul' },
      { value: GambitType.WALK, label: 'gambit.detail.tag.category.walk' },
      { value: GambitType.SHOGI, label: 'gambit.detail.tag.category.shogi' },
      { value: GambitType.GO, label: 'gambit.detail.tag.category.go' },
      { value: GambitType.BACKGAMMON, label: 'gambit.detail.tag.category.backgammon' },
      { value: GambitType.CATAN, label: 'gambit.detail.tag.category.catan' },
      { value: GambitType.SCRABBLE, label: 'gambit.detail.tag.category.scrabble' },
      { value: GambitType.CARDS, label: 'gambit.detail.tag.category.cards' },
      { value: GambitType.BOARD_GAMES, label: 'gambit.detail.tag.category.board_games' },
      { value: GambitType.BEGINNER, label: 'gambit.detail.tag.category.beginner' },
      { value: GambitType.INTERMEDIATE, label: 'gambit.detail.tag.category.intermediate' },
      { value: GambitType.ADVANCED, label: 'gambit.detail.tag.category.advanced' },
      { value: GambitType.LADIES_ONLY, label: 'gambit.detail.tag.category.ladies_only' },
      { value: GambitType.MUSIC, label: 'gambit.detail.tag.category.music' },
      { value: GambitType.CURATED, label: 'gambit.detail.tag.category.curated' },
      { value: GambitType.DONATIONS, label: 'gambit.detail.tag.category.donations' },
    ],
    isStreamingHidden: false,
    isCompact: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: GambitType[]): void;
  (e: 'streamed-selected'): void;
}>();

const selectedTypes = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) {
    return [GambitType.ANY];
  }

  return props.modelValue;
});

const resolvedOptions = computed(() => {
  if (!props.isStreamingHidden) {
    return props.options;
  }

  return props.options.filter((option) => option.value !== GambitType.STREAMED);
});

const toggleGambitType = (nextType: GambitType) => {
  const currentTypes = [...selectedTypes.value];
  let updatedTypes = [...currentTypes];

  if (nextType === GambitType.ANY) {
    updatedTypes = [GambitType.ANY];
  } else {
    updatedTypes = updatedTypes.filter((type) => type !== GambitType.ANY);

    if (updatedTypes.includes(nextType)) {
      updatedTypes = updatedTypes.filter((type) => type !== nextType);
    } else {
      updatedTypes.push(nextType);
    }

    if (updatedTypes.length === 0) {
      updatedTypes = [GambitType.ANY];
    }
  }

  emit('update:modelValue', updatedTypes);

  if (updatedTypes.includes(GambitType.STREAMED)) {
    emit('streamed-selected');
  }
};
</script>

<style scoped lang="scss">
.gambit-type-group {
  border: 1px solid var(--color-white-5);
  border-radius: 1rem;
  padding: var(--spacing-l);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
}

/* Inside a card the group is already framed by its container, so the border and
   outer padding only cost vertical space. */
.gambit-type-group.is-compact {
  border: none;
  border-radius: 0;
  padding: 0;
  gap: var(--spacing-s);
}

.game-type-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: calc(var(--spacing-md) * 1) calc(var(--spacing-md) * 1);

  .gam-chip {
    text-transform: uppercase;
  }
}

.gambit-type-group.is-compact .game-type-row {
  gap: var(--spacing-xs);
}
</style>
